import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useKnowledgeBaseSharing } from '@/hooks/useKnowledgeBaseSharing';

export function useKnowledgeBaseShareOperations(fetchKnowledgeBaseDetails: () => Promise<void>) {
  const { toast } = useToast();
  const {
    shareKnowledgeBase: shareKb,
    updateShareAccessLevel: updateAccess,
    removeShare: remove,
  } = useKnowledgeBaseSharing();

  const handleShareKnowledgeBase = async (
    knowledgeBaseId: string,
    shareEmail: string,
    shareAccessLevel: 'View' | 'Edit' | 'Manage'
  ): Promise<boolean> => {
    if (!knowledgeBaseId || !shareEmail) return false;

    try {
      const { data: usernameProfiles, error: usernameError } = await supabase
        .from('profiles')
        .select('id, username, full_name')
        .eq('username', shareEmail)
        .limit(1);

      if (usernameError) throw usernameError;

      let userProfiles = (usernameProfiles ?? []) as Array<{
        id: string;
        username: string | null;
        full_name: string | null;
      }>;

      if (userProfiles.length === 0) {
        const { data: nameProfiles, error: nameError } = await supabase
          .from('profiles')
          .select('id, username, full_name')
          .eq('full_name', shareEmail)
          .limit(1);

        if (nameError) throw nameError;
        userProfiles = (nameProfiles ?? []) as Array<{
          id: string;
          username: string | null;
          full_name: string | null;
        }>;
      }

      if (userProfiles.length === 0) {
        toast({
          title: 'User not found',
          description: `No user found with identifier: ${shareEmail}`,
          variant: 'destructive',
        });
        return false;
      }

      const targetProfile = userProfiles[0];
      const shared = await shareKb(
        knowledgeBaseId,
        targetProfile.id,
        shareAccessLevel
      );

      if (!shared) {
        return false;
      }

      await fetchKnowledgeBaseDetails();
      return true;
    } catch (error) {
      console.error('Error sharing knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to share knowledge base',
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleUpdateShare = async (
    shareId: string,
    accessLevel: 'View' | 'Edit' | 'Manage'
  ): Promise<void> => {
    const success = await updateAccess(shareId, accessLevel);
    if (success) {
      await fetchKnowledgeBaseDetails();
    }
  };

  const handleRemoveShare = async (shareId: string): Promise<void> => {
    const success = await remove(shareId);
    if (success) {
      await fetchKnowledgeBaseDetails();
    }
  };

  return {
    handleShareKnowledgeBase,
    handleUpdateShare,
    handleRemoveShare,
  };
}
